<template lang="pug">
  app-layout
    v-flex.container.mx-5.mt-4
      .import-progress
        span.headline Importing contacts
        v-icon.contacts-icon.mb-2(color="primary") contacts
        div.alternating-message-container
          transition(name="slide-alternating-message")
            p.alternating-message(
              v-if="showAlternatingMessage"
              v-html="alternatingMessages"
            )
        v-progress-linear.progress(
          :height="20"
          :value="progress"
          color="primary"
        )
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex';
import HotnessOptions from './config/hotness-options';
import ALTERNATING_MESSAGES from './config/alternating-messages';
import { MESSAGE_INTERVAL, MESSAGE_TIMEOUT } from './config/message-interval';
import CONTACT_CHUNK_SIZE from './config/contact-chunk-size';
import { ContactStatusTypes } from '@/Contacts/config/contact-status-types';

const { mapActions: mapContactsTableActions } = createNamespacedHelpers('contactsTable');

const { mapState: mapContactState, mapActions: mapContactActions } =
  createNamespacedHelpers('contact');

const { mapState: mapUserInformationState, mapActions: mapUserInformationActions } =
  createNamespacedHelpers('userInformation');

const { mapState: mapImportContactsState, mapActions: mapImportContactsActions } =
  createNamespacedHelpers('importContacts');

export default {
  name: 'contact-import-progress',
  async created() {
    this.showAlternatingMessage = true;
    this.setMessageInterval();
    if (!this.user) {
      await this.getUser();
    }
    await this.loadContactHotnesses();
    await this.loadContactSources();
    await this.loadContactTypes();
    await this.createSettingsFields();
    await this.saveTags(this.getTagsNoDuplicates).then(async () => {
      await this.importContacts(this.contactsToImport);
    });
  },
  data() {
    return {
      importingContacts: false,
      progress: 0,
      cancelModalShown: false,
      hotnessToCreate: new Set(),
      newSources: new Set(),
      newContactTypes: new Set(),
      messageCounter: 0,
      showAlternatingMessage: false,
    };
  },
  computed: {
    ...mapContactState(['hotnesses', 'broker', 'tags', 'contact', 'contactTypes']),
    ...mapImportContactsState(['contactsToImport', 'addressesToImport']),
    ...mapUserInformationState(['user']),
    ...mapGetters({
      sources: 'contact/sourcesWithDefaultItem',
    }),
    canCreateSources() {
      return (
        this &&
        (!this.broker ||
          this.broker.is_admin ||
          this.broker.permissions.can_create_own_lead_sources === 1)
      );
    },
    canCreateHotness() {
      return (
        this &&
        (!this.broker ||
          this.broker.is_admin ||
          this.broker.permissions.can_create_own_hotnesses === 1)
      );
    },
    canCreateContactTypes() {
      return (
        this &&
        (!this.broker ||
          this.broker.is_admin ||
          this.broker.permissions.can_create_own_contact_types === 1)
      );
    },
    alternatingMessages() {
      const parsedMessage = ALTERNATING_MESSAGES[this.messageCounter].replace(
        '{{userFirstName}}',
        this.user.first_name,
      );
      return parsedMessage;
    },
    getTagsNoDuplicates() {
      const tagsContacts = new Set();
      this.contactsToImport.forEach(contact => {
        if (!contact.tags) return;
        contact.tags.forEach(tag => {
          tagsContacts.add(tag.replace(/\s{2,}/g, ' ').trim());
        });
      });
      return tagsContacts;
    },
  },
  methods: {
    ...mapContactActions([
      'loadContactHotnesses',
      'loadContactSources',
      'loadContactTypes',
      'findBroker',
      'createHotness',
      'createSource',
      'createContact',
      'createTag',
      'createContactType',
    ]),
    ...mapUserInformationActions(['getUser']),
    ...mapImportContactsActions(['setImportResults', 'clearImportResults']),
    // We'll just comment it out for now
    // ...mapContactsTableActions([
    //   'rebuildCacheContacts',
    // ]),
    async saveTags(tagsToSave) {
      const tags = new Set(this.tags);
      tagsToSave.forEach(async tag => {
        if (!tags.has(tag)) {
          await this.createTag(tag);
        }
      });
    },
    async importContacts(contacts) {
      let resolved = 0;
      let errors = 0;
      let success = 0;
      let contactIds = [];
      this.importingContacts = true;
      let failedRecords = [];

      // Chunk array of contacts to optimize sequential requests
      const chunks = [];
      let contactPosition = 0;

      while (contactPosition < contacts.length) {
        chunks.push(contacts.slice(contactPosition, (contactPosition += CONTACT_CHUNK_SIZE)));
      }

      chunks
        .reduce(
          (currentPromise, chunk, reduceLoopIndex) =>
            currentPromise.then(() => {
              return Promise.all(
                chunk.map(async (contact, contactIndex) => {
                  const importContact = this.prepareImportContact(
                    contact,
                    reduceLoopIndex,
                    contactIndex,
                  );
                  const SPECIAL_CHARACTERS_REGEX = /[#,+()$~%.':*?<>{}]/g;
                  const SPECIAL_CHARACTERS_FOR_EMAILS_REGEX = /[\[\]\/\\#,()$~%'":*?<>{}\s^]/g;
                  Object.keys(importContact).map(attr => {
                    if (attr === 'tags') return;
                    if (importContact[attr] instanceof Array) {
                      for (i = 0; i < importContact[attr].length; i++) {
                        if (SPECIAL_CHARACTERS_REGEX.test(importContact[attr][i])) {
                          importContact[attr][i] = importContact[attr][i].replace(
                            SPECIAL_CHARACTERS_REGEX,
                            '',
                          );
                        }
                      }
                    } else if (attr === 'email' || attr === 'spouse_email' || attr === 'secondary_email') {
                      importContact[attr] = importContact[attr].replace(
                        SPECIAL_CHARACTERS_FOR_EMAILS_REGEX,
                        '',
                      );
                    } else if (attr === 'status') {

                      const contactType = ContactStatusTypes.find(type => 
                        type.name.toLowerCase() == importContact[attr].trim().toLowerCase()
                      );

                      importContact[attr] = contactType && contactType.name;

                    } else if (SPECIAL_CHARACTERS_REGEX.test(importContact[attr])) {
                      importContact[attr] = importContact[attr].replace(
                        SPECIAL_CHARACTERS_REGEX,
                        '',
                      );
                    }
                  });
                  importContact.no_cache = true;
                  return this.createContact(importContact)
                    .then(() => {
                      contactIds.push(this.contact.id);
                      success += 1;
                    })
                    .catch(error => {
                      if (importContact['hotness_id'])
                        importContact['hotness_value'] = contact['hotness_id'];
                      if (importContact['source_id'])
                        importContact['source_value'] = contact['source_id'];
                      if (!error || !error.code) {
                        importContact['error_message'] =
                          'There was an internet connection error, please try again later.';
                      } else {
                        importContact['error_message'] =
                          error.errors && error.errors.length ? error.errors[0] : error.message;
                      }
                      failedRecords.push(importContact);
                      errors += 1;
                    })
                    .finally(() => {
                      resolved += 1;
                      this.progress = (resolved / contacts.length) * 100;
                    });
                }),
              );
            }),
          Promise.resolve([]),
        )
        .then(() => {
          // We'll just comment it out for now
          // this.rebuildCacheContacts(contactIds);
          console.info(`Finished batch import with ${errors} errors`);
          this.importingContacts = false;
          this.progress = 100;
          this.setImportResults({ success, errors, failedRecords });
          this.$router.push('/contacts/import/summary');
        });
    },
    trimStringValues(contact) {
      return Object.keys(contact).reduce(function (result, key) {
        result[key] = typeof contact[key] == 'string' ? contact[key].trim() : contact[key];
        return result;
      }, {});
    },
    prepareImportContact(contact, reduceLoopIndex, contactIndex) {
      const importContact = { ...this.trimStringValues(contact) };
      const hotnessId = this.getHotnessId(importContact);
      if (hotnessId) {
        importContact.hotness_id = hotnessId;
      } else {
        delete importContact.hotness_id;
      }

      const sourceId = this.getSourceId(importContact);
      if (sourceId) {
        importContact.source_id = sourceId;
      } else {
        delete importContact.source_id;
      }

      const contactTypeId = this.getContactTypeId(importContact);
      if (contactTypeId) {
        importContact.type_id = contactTypeId;
      } else {
        delete importContact.type_id;
      }

      importContact.addresses = this.addressesToImport.map(address => {
        return address[contactIndex + CONTACT_CHUNK_SIZE * reduceLoopIndex];
      }).filter(address => {
        return Object.entries(address).some(([key, value]) => {
          return key !== "type" && value && value.length !== 0 
        });
      });

      return importContact;
    },
    createSettingsFields() {
      const settingsCalls = [];
      this.contactsToImport.forEach(contact => {
        if (this.canCreateHotness) {
          const hotnessCall = this.createHotnessCall(contact);
          if (hotnessCall) {
            settingsCalls.push(hotnessCall);
          }
        }

        if (this.canCreateSources) {
          const sourceCall = this.createSourceCall(contact);
          if (sourceCall) {
            settingsCalls.push(sourceCall);
          }
        }

        if (this.canCreateContactTypes) {
          const contactType = this.createContactTypeCall(contact);
          if (contactType) {
            settingsCalls.push(contactType);
          }
        }
      });
      return Promise.all(settingsCalls.map(call => call.catch(e => console.log(e))));
    },
    createHotnessCall(contact) {
      if (contact.hotness_id && contact.hotness_id.trim() !== '') {
        const hotnessName = contact.hotness_id.trim();
        if (!this.hotnessToCreate.has(hotnessName.toLowerCase())) {
          let hotness = this.hotnesses.find(hotnessValue => {
            return hotnessValue.text.toLowerCase() === hotnessName.toLowerCase();
          });
          if (!hotness) {
            this.hotnessToCreate.add(hotnessName.toLowerCase());
            hotness = { name: hotnessName, ...HotnessOptions };
            return this.createHotness(hotness);
          }
        }
      }
      return null;
    },
    createSourceCall(contact) {
      if (contact.source_id && contact.source_id.trim()) {
        const sourceName = contact.source_id.trim();
        if (!this.newSources.has(sourceName.toLowerCase())) {
          let source = this.sources.find(sourceValue => {
            return sourceValue.text.toLowerCase() === sourceName.toLowerCase();
          });
          if (!source) {
            this.newSources.add(sourceName.toLowerCase());
            source = { name: sourceName };
            return this.createSource(source);
          }
        }
      }
      return null;
    },
    createContactTypeCall(contact) {
      if (!contact.type_id || !contact.type_id.trim()) return null;

      const contactTypeName = contact.type_id.trim();
      if (this.newContactTypes.has(contactTypeName.toLowerCase())) return null;

      let contactType = this.contactTypes.find(typeValue => {
        return typeValue.text.toLowerCase() === contactTypeName.toLowerCase();
      });
      if (!contactType) {
        this.newContactTypes.add(contactTypeName.toLowerCase());
        return this.createContactType({ name: contactTypeName });
      }
    },
    getHotnessId(contact) {
      if (!contact.hotness_id || contact.hotness_id.trim() === '') {
        return 0;
      }
      const hotnessName = contact.hotness_id.trim();
      const hotness = this.hotnesses.find(hotnessValue => {
        return hotnessValue.text.toLowerCase() === hotnessName.toLowerCase();
      });
      if (hotness) {
        return hotness.value;
      }
      return 0;
    },
    getSourceId(contact) {
      if (!contact.source_id || !contact.source_id.trim()) {
        return 0;
      }
      const sourceName = contact.source_id.trim();
      const source = this.sources.find(sourceValue => {
        return sourceValue.text.toLowerCase() === sourceName.toLowerCase();
      });
      if (source) {
        return source.value;
      }
      return 0;
    },
    getContactTypeId(contact) {
      if (!contact.type_id || contact.type_id.trim() === '') {
        return 0;
      }
      const contactTypeName = contact.type_id.trim();
      const contactType = this.contactTypes.find(typeValue => {
        return typeValue.text.toLowerCase() === contactTypeName.toLowerCase();
      });
      return contactType ? contactType.value : 0;
    },
    setMessageInterval() {
      setInterval(() => {
        this.showAlternatingMessage = false;
        this.messageCounter =
          this.messageCounter < ALTERNATING_MESSAGES.length - 1 ? this.messageCounter + 1 : 0;
        setTimeout(() => {
          this.showAlternatingMessage = true;
        }, MESSAGE_TIMEOUT);
      }, MESSAGE_INTERVAL);
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  align-items: center;
  display: flex;
  width: 100%;
  flex: inherit;
  flex-direction: column;
  .import-progress {
    width: 70%;
    max-width: 1600px;
    display: flex;
    flex-direction: column;
  }
}
.headline {
  font-size: 24px;
  margin-bottom: 48px;
}
.contacts-icon {
  font-size: 64px;
  align-self: center;
}

.cancel-btn {
  width: 120px;
  align-self: flex-end;
}

p {
  margin: 36px 0;
  font-size: 16px;
  text-align: center;
}

.progress {
  width: 70%;
  margin: 0 auto;
  position: relative;
}

.slide-alternating-message-enter-active,
.slide-alternating-message-leave-active {
  transition: all 0.3s ease;
}

.slide-alternating-message-enter {
  transform: translateX(-100px);
  opacity: 0;
}
.slide-alternating-message-leave-to {
  transform: translateX(100px);
  opacity: 0;
}
.alternating-message-container {
  height: 110px;
  width: 75%;
  margin: 2.5% auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 500px;
  p {
    text-align: center;
    margin: 0;
  }
}
</style>
